import { useState, useEffect, useCallback } from "react";
import socketIO from "socket.io-client";

const useMonitorSocket = (messages, setMessages, fetchRooms) => {
    const [socket, setSocket] = useState(null);
    const [queuesByRoom, setQueuesByRoom] = useState({});
    const [soundsQueue, setSoundsQueue] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);

    // טעינת קבצי האודיו
    // טעינת קבצי האודיו פעם אחת בלבד
    const audioFiles = useState(() => {
        const files = {
            0: new Audio("/sounds/0.mp3"),
            1: new Audio("/sounds/1.mp3"),
            2: new Audio("/sounds/2.mp3"),
            3: new Audio("/sounds/3.mp3"),
            4: new Audio("/sounds/4.mp3"),
            5: new Audio("/sounds/5.mp3"),
            6: new Audio("/sounds/6.mp3"),
            7: new Audio("/sounds/7.mp3"),
            8: new Audio("/sounds/8.wav"),
            9: new Audio("/sounds/9.mp3"),
            patientNumber: new Audio("/sounds/patientNumber.mp3"),
            toRoom: new Audio("/sounds/comeToRoomNumber.mp3"),
            reception: new Audio("/sounds/reception.mp3"),
        };

        // הוספת קובצי אודיו לכל המספרים מ-10 עד 140
        for (let i = 10; i <= 140; i++) {
            files[i] = new Audio(`/sounds/${i}.mp3`);
        }

        // מגדיר טעינה מראש לכל האודיו
        Object.values(files).forEach(audio => {
            audio.preload = "auto";
        });

        return files;
    })[0];
   

    // פונקציה לטעינת אודיו עם בדיקות טעינה
    const loadAudio = (audio, retries = 3) => {
        return new Promise((resolve, reject) => {
            let attempts = 0;

            const checkLoaded = () => {
                if (audio.readyState >= 4) {
                    resolve();
                } else if (attempts < retries) {
                    attempts++;
                    console.log(`Retrying to load audio: ${audio.src}, attempt ${attempts}`);
                    audio.load(); // מנסה לטעון מחדש
                    setTimeout(checkLoaded, 1000); // מחכה שנייה ומנסה שוב
                } else {
                    reject(`Failed to load audio: ${audio.src}`);
                }
            };

            audio.addEventListener("canplaythrough", resolve, { once: true });
            audio.addEventListener("error", () => {
                console.error(`Error loading audio: ${audio.src}`);
                checkLoaded();
            });

            checkLoaded();
        });
    };

    // טוען את כל קובצי האודיו מראש
    const preloadAllAudio = async () => {
        try {
            await Promise.all(Object.values(audioFiles).map(audio => loadAudio(audio)));
            console.log("All audio files loaded successfully");
        } catch (error) {
            console.error("Error preloading audio files:", error);
        }
    };

    useEffect(() => {
        preloadAllAudio();

        const newSocket = socketIO(process.env.REACT_APP_SERVICE_URL, {
            query: { clientId: "monitor" },
        });
        setSocket(newSocket);

        newSocket.on("connect", () => {
            console.log("Connected to WebSocket server");
        });

        newSocket.on("playAudio", (queueNumber, roomNumber) => {
            insertSoundToQueue(roomNumber, queueNumber);
        });

        newSocket.on("disconnect", (reason) => {
            console.log("Disconnected from WebSocket server:", reason);
        });

        newSocket.on("queueUpdate", (roomId, newQueue) => {
            setQueuesByRoom((prevQueues) => ({
                ...prevQueues,
                [roomId]: newQueue,
            }));
        });

        newSocket.on("roomUpdate", fetchRooms);

        newSocket.on("deleteAllData", () => {
            window.location.reload();
        });

        newSocket.on("messageUpdated", (message) => {
            const activeMessages = message.filter((msg) => msg.Status === true);
            setMessages(activeMessages);
        });

        return () => {
            newSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!isPlaying && soundsQueue.length > 0) {
            playQueueSounds();
        }
    }, [soundsQueue, isPlaying]);

    const insertSoundToQueue = (roomNumber, queueNumber) => {
        setSoundsQueue((prevQueue) => [...prevQueue, { roomNumber, queueNumber }]);
    };

    const subscribeToRoom = useCallback(
        (roomId) => {
            if (socket && !queuesByRoom[roomId]) {
                socket.emit("subscribeToQueue", roomId);
            }
        },
        [socket]
    );

    const playAudio = (audio) => {
        return new Promise((resolve, reject) => {
            loadAudio(audio) // מוודא שהאודיו נטען במלואו לפני הניגון
                .then(() => {
                    audio.currentTime = 0;
                    const playPromise = audio.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                audio.onended = resolve;
                                audio.onerror = reject;
                            })
                            .catch(reject);
                    }
                })
                .catch(reject);
        });
    };

    const playQueueSounds = async () => {
        if (soundsQueue.length === 0 || isPlaying) return;

        setIsPlaying(true);
        const { roomNumber, queueNumber } = soundsQueue[0];
        console.log(`Playing queue number ${queueNumber} for room ${roomNumber}`);

        try {
            await playAudio(audioFiles.patientNumber);
            if (audioFiles[queueNumber]) {
                // אם יש קובץ אודיו ישיר למספר הזה (עד 140)
                await playAudio(audioFiles[queueNumber]);
            } else {
                // אם אין הקלטה מלאה - פיצול לספרות
                const queueDigits = queueNumber.toString().split("");
                for (const digit of queueDigits) {
                    await playAudio(audioFiles[digit]);
                }
            }
            if (roomNumber === 0) {
                await playAudio(audioFiles.reception);
            } else {
                await playAudio(audioFiles.toRoom);
                await playAudio(audioFiles[roomNumber]);
            }
        } catch (error) {
            console.error("Error playing audio:", error);
        }

        setSoundsQueue((prevQueue) => prevQueue.slice(1));
        setIsPlaying(false);
    };

    return { subscribeToRoom, queuesByRoom, socket, messages };
};

export default useMonitorSocket;
